import type { FocusTrap } from '@angular/cdk/a11y';
import { FocusTrapFactory } from '@angular/cdk/a11y';
import { NgTemplateOutlet } from '@angular/common';
import type { AfterViewInit, OnDestroy } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import type { SafeHtml } from '@angular/platform-browser';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faFacebookF, faLinkedinIn, faThreads, faWhatsapp, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import { faCommentSms, faCopy, faEnvelope, faTimes } from '@fortawesome/free-solid-svg-icons';

/**
 * Which screen the widget shows. Driven entirely by the parent. `'loading'` is the initial
 * placeholder shown while the parent resolves the user's login/link state, so the intake form
 * never flashes before flipping to the share screen.
 */
export type ReferAFriendScreen = 'intake' | 'link' | 'loading';

/** Theming hint passed in by the parent — this component never resolves brand/env state itself. */
export type ReferAFriendDesignMode = 'ace' | 'default';

/** Fixed, Figma-ordered social share channels for the link/share screen. */
export type ReferAFriendSocialChannelId = 'email' | 'whatsapp' | 'facebook' | 'threads' | 'x' | 'linkedin' | 'text';

/**
 * Hard cap on how many share channels render at once. The Craft "Social Options" field limits
 * authors to this many selections; the widget also slices to it so an over-configured global can
 * never blow out the share row.
 */
const MAX_VISIBLE_CHANNELS = 5;

/**
 * All copy is brand-owned and supplied by the parent — this component holds no brand copy of
 * its own. The six rich-text fields accept `SafeHtml` (Craft authors them as CKEditor and the
 * parent passes them as trusted `SafeHtml` via `bypassSecurityTrustHtml` — Angular sanitization
 * is bypassed, so the copy source must be trusted) and render via `[innerHTML]`; the four
 * label/placeholder fields stay plain strings. `string` remains assignable so the plain-string
 * defaults below still satisfy it.
 */
export interface ReferAFriendCopy {
  title: string | SafeHtml;
  body: string | SafeHtml;
  incentiveText: string | SafeHtml;
  ctaLabel: string;
  shareHeading: string | SafeHtml;
  emailPlaceholder: string;
  invalidEmailMessage: string | SafeHtml;
  copyButtonLabel: string;
  copiedLabel: string;
  retryLabel: string | SafeHtml;
}

/**
 * One Craft/config-driven social share entry (the legacy XCC path). When the parent supplies these,
 * they drive which channels show, their order, label, and icon image; the widget still computes the
 * share `href` from the live referral link for known channels (a static URL cannot carry a per-user
 * link), using `url` only when it is a non-blank override.
 *
 * Vision (core-mkt) instead uses the lighter `selectedChannels` id list — colours/icons/urls/labels
 * are all hardcoded there. `shareLinks` is retained for the XCC post-checkout container, which feeds
 * it from S3 page-config.
 */
export interface ReferAFriendShareLink {
  label: string;
  url?: string;
  iconUrl?: string;
  iconAlt?: string;
  // Optional author-set background color (hex) for the channel's circle. Overrides the computed
  // Figma tint when set; blank/absent keeps the default tint.
  bgColor?: string;
}

export const DEFAULT_REFER_A_FRIEND_COPY: ReferAFriendCopy = {
  title: 'Refer a friend',
  body: 'Share your referral link with friends — when they sign up, you could both earn a reward.',
  incentiveText: '',
  ctaLabel: 'Get my referral link',
  shareHeading: 'Share your link',
  emailPlaceholder: 'Enter your email address',
  invalidEmailMessage: 'Please enter a valid email address.',
  copyButtonLabel: 'Copy link',
  copiedLabel: 'Copied!',
  retryLabel: 'Try again',
};

interface SocialShareChannel {
  // A known ReferAFriendSocialChannelId, or a slug derived from a Craft label the widget doesn't
  // recognize (legacy XCC `shareLinks` path) — both are strings, so the field is typed as `string`.
  id: string;
  label: string;
  // FontAwesome icon for the computed-default row; absent when a Craft-supplied image is used (XCC).
  icon?: IconProp;
  // Craft-supplied icon image (takes precedence over `icon` when present) — XCC path only.
  iconUrl?: string;
  iconAlt?: string;
  href: string;
  /**
   * Tailwind classes for the circle's tinted background + glyph colour (Figma: the network colour at
   * 10%, or #21333F at 5% for the monochrome glyphs). These live here rather than in the template
   * because the template composes the channel class dynamically (`raf-social-icon--{{ id }}`) and
   * Tailwind only generates classes it can find as literal strings — which it does in this file, since
   * the brand presets' `content` globs cover `libs/shared-ui/**\/*.ts`. Empty for an unrecognised
   * Craft-supplied channel (it has no Figma tint), matching the previous SCSS behaviour.
   */
  iconClasses: string;
  // Optional author-set background color (hex, XCC path). When present it's applied as an inline style
  // on the circle, overriding the `iconClasses` tint background; the glyph colour is preserved.
  bgColor?: string;
}

const COPIED_CONFIRMATION_MS = 2000;

/**
 * Presentational Refer-A-Friend modal widget (email intake -> referral-link/share).
 *
 * Deliberately has zero knowledge of brand resolution, environment, or the Ambassador API —
 * every brand/theme/copy value arrives via @Input(), and every user action is surfaced via
 * @Output() for a parent to wire up. This keeps the component liftable into a shared lib
 * unchanged once a later ticket wires it to a live integration.
 *
 * Parent integration contract (for the ticket that wires this to a live integration):
 * - The parent owns `screen` and switches it to `'link'` only after `submitEmail` resolves
 *   successfully — this component never transitions itself.
 * - `loading`/`error` describe the parent's in-flight API call; this component has no async
 *   state of its own beyond the local "copied" confirmation.
 * - `referralLink` must be set before `screen` flips to `'link'` — the share screen renders
 *   whatever string it's given, including an empty one, without validating it.
 * - `retry`/`closeModal` are pure signals back to the parent; this component takes no action
 *   on emit beyond firing the output.
 * - Focus RESTORE on close is the parent/mount layer's job, not this component's: whatever
 *   opened the modal (a trigger button, a menu item) should refocus itself after `closeModal`
 *   fires. This component owns focus only while it is open — moving focus in on open/screen
 *   change and trapping Tab/Shift+Tab within the card — because it has no visibility into
 *   what element originally opened it.
 */
@Component({
  selector: 'refer-a-friend',
  templateUrl: './refer-a-friend.component.html',
  styleUrls: ['./refer-a-friend.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, FontAwesomeModule, NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferAFriendComponent implements AfterViewInit, OnDestroy {
  readonly screen = input<ReferAFriendScreen>('intake');
  /**
   * How the widget is presented. `'modal'` (default) keeps the original overlay/dialog
   * treatment untouched for the Vision trigger flow. `'inline'` renders the same card in
   * document flow with the overlay backdrop, dialog semantics, close control, focus trap,
   * and Escape-to-close removed — used where the widget is embedded directly in a page
   * (e.g. XCC post-checkout) rather than launched over one.
   */
  readonly variant = input<'modal' | 'inline'>('modal');
  readonly copy = input<ReferAFriendCopy>(DEFAULT_REFER_A_FRIEND_COPY);
  readonly referralLink = input<string>('');
  readonly loading = input<boolean>(false);
  readonly error = input<string | null>(null);
  readonly designMode = input<ReferAFriendDesignMode>('default');
  // Legacy XCC path: rich Craft/config-driven share row (label + optional url/icon image/bgColor).
  // Empty/null → fall back to the computed FontAwesome channels. Vision uses `selectedChannels` instead.
  readonly shareLinks = input<ReferAFriendShareLink[] | null>(null);
  // Vision path: which channels show, by id (`email`, `whatsapp`, `facebook`, `threads`, `x`), in display
  // order. Empty/null → the full computed default set. Unknown ids are ignored. Colours, icons, labels, and
  // hrefs are all hardcoded (FontAwesome glyphs) — this only picks which/what order. Takes precedence over
  // `shareLinks` when both are set.
  readonly selectedChannels = input<string[] | null>(null);
  // Plain-text share message the parent derives from the (rich-text) incentive/title copy. Used
  // for the share-intent hrefs because `copy().incentiveText`/`title` may be SafeHtml, which would
  // serialize to "[object Object]" through encodeURIComponent.
  readonly shareMessage = input<string>('');

  readonly submitEmail = output<string>();
  readonly retry = output<void>();
  readonly closeModal = output<void>();
  readonly copyLink = output<void>();

  readonly faTimes = faTimes;
  readonly faCopy = faCopy;

  // Static, per-screen dialog label. Kept plain (not the brand copy) both because the rich copy
  // fields are now SafeHtml — which would serialize to "[object Object]" in an attribute — and to
  // match the widget's other hardcoded a11y strings (`Close`, `Email address`).
  readonly dialogLabel = computed<string>(() => {
    switch (this.screen()) {
      case 'loading':
        return 'Loading';
      case 'link':
        return 'Share your referral link';
      default:
        return 'Refer a friend';
    }
  });

  readonly copied = signal(false);
  readonly submitAttempted = signal(false);

  @ViewChild('rafCard') private readonly cardRef?: ElementRef<HTMLElement>;

  private readonly formBuilder = inject(FormBuilder);
  private readonly focusTrapFactory = inject(FocusTrapFactory);
  private copiedTimeoutId: ReturnType<typeof setTimeout> | undefined;
  private focusTrap?: FocusTrap;

  readonly emailForm = this.formBuilder.group({
    email: ['', [Validators.required, Validators.email]],
  });

  constructor() {
    // Registered with the library and looked up by [prefix, name] tuple (IconProp) rather
    // than bound as directly-typed IconDefinition values — the same pattern
    // footer-link.component.ts uses.
    //
    // This started as a workaround: the icon packs used to ship their own nested copy of
    // fontawesome-common-types, whose IconName union diverged from the hoisted one solid
    // icons resolved against, so mixing the two packages in one addIcons() call failed to
    // compile. EPS-4819 aligned the family on 7.3.1 and deduped common-types to a single
    // hoisted copy, so that constraint is gone. The tuple indirection is kept because it
    // also decouples this component from the icon packs at the type level, but a direct
    // IconDefinition binding would now compile fine if someone wants to simplify.
    inject(FaIconLibrary).addIcons(
      faEnvelope,
      faWhatsapp,
      faFacebookF,
      faThreads,
      faXTwitter,
      faLinkedinIn,
      faCommentSms,
    );

    // The card element persists across intake<->link screen changes (only the @if content
    // inside it toggles), so the CDK FocusTrap set up in ngAfterViewInit keeps trapping Tab
    // continuously on its own. What it does NOT do on its own is re-focus when the *visible
    // screen* changes without the trap itself re-initializing — so re-run the "move focus to
    // the first tabbable element" step explicitly whenever `screen` changes.
    effect(() => {
      this.screen();
      void this.focusTrap?.focusFirstTabbableElementWhenReady();
    });
  }

  ngAfterViewInit(): void {
    if (!this.cardRef) {
      return;
    }
    this.focusTrap = this.focusTrapFactory.create(this.cardRef.nativeElement);
    void this.focusTrap.focusFirstTabbableElementWhenReady();
  }

  /**
   * The default, computed share row keyed by channel id. Share hrefs are always built from the
   * live `referralLink` (a per-user value a static Craft URL cannot carry) and the plain-text
   * `shareMessage`. Figma order — Email, WhatsApp, Facebook, Threads, X — then the opt-in
   * LinkedIn and Text, which only surface when an author selects them via `selectedChannels`.
   */
  private readonly computedChannels = computed<Record<ReferAFriendSocialChannelId, SocialShareChannel>>(() => {
    const encodedLink = encodeURIComponent(this.referralLink());
    const encodedText = encodeURIComponent(this.shareMessage());
    return {
      email: {
        id: 'email',
        label: 'Email',
        icon: ['fas', 'envelope'],
        href: `mailto:?subject=${encodedText}&body=${encodedText}%20${encodedLink}`,
        iconClasses: 'bg-[#2689ca]/10 text-[#2689ca]',
      },
      whatsapp: {
        id: 'whatsapp',
        label: 'WhatsApp',
        icon: ['fab', 'whatsapp'],
        href: `https://wa.me/?text=${encodedText}%20${encodedLink}`,
        iconClasses: 'bg-[#25d366]/10 text-[#25d366]',
      },
      facebook: {
        id: 'facebook',
        label: 'Facebook',
        icon: ['fab', 'facebook-f'],
        href: `https://www.facebook.com/sharer/sharer.php?u=${encodedLink}`,
        iconClasses: 'bg-[#1877f2]/10 text-[#1877f2]',
      },
      threads: {
        id: 'threads',
        label: 'Threads',
        icon: ['fab', 'threads'],
        href: `https://www.threads.net/intent/post?text=${encodedText}%20${encodedLink}`,
        iconClasses: 'bg-[#21333f]/5 text-[#21333f]',
      },
      x: {
        id: 'x',
        label: 'X',
        icon: ['fab', 'x-twitter'],
        href: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedLink}`,
        iconClasses: 'bg-[#21333f]/5 text-[#21333f]',
      },
      linkedin: {
        id: 'linkedin',
        label: 'LinkedIn',
        icon: ['fab', 'linkedin-in'],
        href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedLink}`,
        iconClasses: 'bg-[#0a66c2]/10 text-[#0a66c2]',
      },
      // SMS deep-link: `sms:?&body=` is the cross-platform form iOS + Android both honour. It only
      // does anything on a device with a messaging app (a no-op on most desktops) — expected.
      text: {
        id: 'text',
        label: 'Text',
        icon: ['fas', 'comment-sms'],
        href: `sms:?&body=${encodedText}%20${encodedLink}`,
        iconClasses: 'bg-[#34c759]/10 text-[#34c759]',
      },
    };
  });

  /**
   * The share row actually rendered. Precedence:
   * 1. Vision `selectedChannels` (ids) — pick which hardcoded channels show and in what order; unknown
   *    ids are dropped. Colours/icons/labels/hrefs are all hardcoded (FontAwesome glyphs, no images).
   * 2. Legacy XCC `shareLinks` (labels) — each drives presence/order/label/icon image; the computed href
   *    is reused (Craft's `url` only overrides when non-blank); an entry whose label resolves to no known
   *    channel and carries no explicit `url` is dropped (no working share target could be built).
   * 3. Neither → the full computed default set (Figma order).
   */
  readonly shareChannels = computed<SocialShareChannel[]>(() => {
    const computedById = this.computedChannels();

    // The template `@for` tracks by `channel.id`, so any duplicate id (a config repeating an id, or two
    // Craft labels resolving to the same channel/slug) would throw NG0955 and drop the whole row. Dedupe
    // by id in both paths, keeping the first occurrence to preserve author order.
    const seen = new Set<string>();

    const selected = this.selectedChannels();
    if (selected?.length) {
      return selected
        .reduce<SocialShareChannel[]>((channels, id) => {
          const channel = computedById[id as ReferAFriendSocialChannelId];
          if (channel && !seen.has(channel.id)) {
            seen.add(channel.id);
            channels.push(channel);
          }
          return channels;
        }, [])
        .slice(0, MAX_VISIBLE_CHANNELS);
    }

    const craftLinks = this.shareLinks();
    if (!craftLinks?.length) {
      // Default set is the first MAX_VISIBLE_CHANNELS of the Figma-ordered map — LinkedIn/Text are
      // opt-in via `selectedChannels`, so an unconfigured widget still shows the original five.
      return Object.values(computedById).slice(0, MAX_VISIBLE_CHANNELS);
    }

    return craftLinks
      .reduce<SocialShareChannel[]>((channels, craftLink) => {
        const resolvedId = this.resolveChannelId(craftLink.label);
        const base = resolvedId ? computedById[resolvedId] : undefined;
        // Craft's `url` is CMS-driven and rendered straight into an anchor href, so reject any unsafe
        // scheme (e.g. `javascript:`/`data:`) before it can override the computed share link.
        const overrideUrl = craftLink.url?.trim();
        const safeOverride = overrideUrl && this.isSafeShareUrl(overrideUrl) ? overrideUrl : undefined;
        const href = safeOverride || base?.href;
        if (!href) {
          return channels;
        }
        const id = resolvedId ?? this.slugify(craftLink.label);
        if (seen.has(id)) {
          return channels;
        }
        seen.add(id);
        channels.push({
          id,
          label: craftLink.label,
          icon: craftLink.iconUrl ? undefined : base?.icon,
          iconUrl: craftLink.iconUrl,
          iconAlt: craftLink.iconAlt,
          href,
          // A known channel keeps its Figma tint even when Craft supplies its own icon image; an
          // unrecognised label has no tint (same as the previous SCSS, which had no rule for it).
          iconClasses: base?.iconClasses ?? '',
          // Author-set background color overrides the tint via inline style in the template.
          bgColor: craftLink.bgColor,
        });
        return channels;
      }, [])
      .slice(0, MAX_VISIBLE_CHANNELS);
  });

  private resolveChannelId(label: string): ReferAFriendSocialChannelId | null {
    const normalized = label.trim().toLowerCase();
    switch (normalized) {
      case 'email':
        return 'email';
      case 'whatsapp':
        return 'whatsapp';
      case 'facebook':
        return 'facebook';
      case 'threads':
        return 'threads';
      case 'x':
      case 'twitter':
        return 'x';
      case 'linkedin':
        return 'linkedin';
      case 'text':
        return 'text';
      default:
        return null;
    }
  }

  // Schemes allowed for a Craft-supplied `shareLink.url` override. The computed hrefs only ever use
  // https/mailto/sms; anything with another explicit scheme (javascript:, data:, vbscript:, …) is rejected.
  private static readonly SAFE_URL_PROTOCOLS = ['http:', 'https:', 'mailto:', 'sms:'];

  private isSafeShareUrl(url: string): boolean {
    // Protocol-relative (`//host`) has no scheme but still navigates cross-origin, so reject it up front
    // — a CMS-supplied `//evil.example` would otherwise slip through the scheme-less allow below.
    if (/^\s*\/\//.test(url)) {
      return false;
    }
    // A remaining scheme-less value (relative path) carries no dangerous protocol, so allow it; only gate
    // values that declare an explicit scheme. Matches RFC 3986 scheme syntax (letter, then letters/digits/+-.).
    const scheme = url.match(/^([a-z][a-z0-9+.-]*):/i);
    if (!scheme) {
      return true;
    }
    return ReferAFriendComponent.SAFE_URL_PROTOCOLS.includes(scheme[1].toLowerCase() + ':');
  }

  private slugify(label: string): string {
    return label
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  get showEmailError(): boolean {
    const control = this.emailForm.get('email');
    if (!control) {
      return false;
    }
    return control.invalid && (control.touched || this.submitAttempted());
  }

  onSubmit(): void {
    this.submitAttempted.set(true);
    const control = this.emailForm.get('email');
    if (!control) {
      return;
    }
    // Trim before validating, not after — Validators.email's anchored regex rejects any
    // leading/trailing whitespace, so a post-validation trim would never run for exactly
    // the input it exists to handle.
    const trimmed = String(control.value ?? '').trim();
    if (trimmed !== control.value) {
      control.setValue(trimmed);
    }
    if (control.invalid) {
      control.markAsTouched();
      return;
    }
    this.submitEmail.emit(trimmed);
  }

  onCopyLink(): void {
    const link = this.referralLink();
    if (!link || typeof navigator === 'undefined') {
      return;
    }

    const clipboard = navigator.clipboard;
    if (clipboard?.writeText) {
      clipboard
        .writeText(link)
        .then(() => this.confirmCopied())
        // The async Clipboard API rejects on permission denial or a non-secure (http) context;
        // fall back to the legacy selection copy so the user still gets their link + confirmation.
        .catch(() => this.fallbackCopy(link));
      return;
    }

    // No async Clipboard API at all (older browser / non-secure http context): legacy fallback.
    this.fallbackCopy(link);
  }

  private confirmCopied(): void {
    this.copied.set(true);
    this.copyLink.emit();
    this.clearCopiedTimeout();
    this.copiedTimeoutId = setTimeout(() => this.copied.set(false), COPIED_CONFIRMATION_MS);
  }

  private fallbackCopy(link: string): void {
    if (this.copyViaExecCommand(link)) {
      this.confirmCopied();
    } else {
      this.copied.set(false);
    }
  }

  /**
   * Legacy clipboard fallback for contexts where the async Clipboard API is unavailable or
   * rejects (non-secure http, older browsers). Copies via a hidden textarea + `execCommand` and
   * returns whether the copy succeeded. `onCopyLink` already guards `typeof navigator`, so
   * `document` is present here (the same browser context).
   */
  private copyViaExecCommand(link: string): boolean {
    const textarea = document.createElement('textarea');
    textarea.value = link;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    let succeeded: boolean;
    try {
      succeeded = document.execCommand('copy');
    } catch {
      succeeded = false;
    }
    textarea.remove();
    return succeeded;
  }

  onRetry(): void {
    this.retry.emit();
  }

  onClose(): void {
    this.closeModal.emit();
  }

  // Matches the Escape-to-dismiss convention other core-mkt modals (e.g. exit-intent-modal) use.
  // Only meaningful for the modal variant — an inline embed is part of the page, not a dismissible
  // overlay, so it must not react to Escape.
  @HostListener('document:keydown.escape')
  onEscapeKeydown(): void {
    if (this.variant() !== 'modal') {
      return;
    }
    this.onClose();
  }

  ngOnDestroy(): void {
    this.clearCopiedTimeout();
    this.focusTrap?.destroy();
  }

  private clearCopiedTimeout(): void {
    if (this.copiedTimeoutId !== undefined) {
      clearTimeout(this.copiedTimeoutId);
      this.copiedTimeoutId = undefined;
    }
  }
}
